import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Dropdown, Icon, Label } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API } from '../helpers';
import './BillingDateCalendarV89.css';

export const localDateKeyV89 = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const localDateFromKeyV89 = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
  return localDateKeyV89(date) === key ? date : null;
};

const addLocalDaysV89 = (date, amount) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);

const datesBetweenV89 = (start, end) => {
  const forward = start <= end;
  const first = forward ? start : end;
  const last = forward ? end : start;
  const dates = [];
  for (
    let cursor = first;
    cursor <= last;
    cursor = addLocalDaysV89(cursor, 1)
  ) {
    dates.push(localDateKeyV89(cursor));
  }
  return dates;
};

const billingBoundaryForMonthV89 = (year, month, billingDay) => {
  const finalDay = new Date(year, month + 1, 0).getDate();
  return new Date(
    year,
    month,
    Math.min(Math.max(Number(billingDay) || 1, 1), finalDay)
  );
};

export const billingCycleDateKeysV89 = (date, billingDay, previous = false) => {
  const boundary = billingBoundaryForMonthV89(
    date.getFullYear(),
    date.getMonth(),
    billingDay
  );
  let start;
  let end;
  if (date < boundary) {
    start = billingBoundaryForMonthV89(
      date.getFullYear(),
      date.getMonth() - 1,
      billingDay
    );
    end = boundary;
  } else {
    start = boundary;
    end = billingBoundaryForMonthV89(
      date.getFullYear(),
      date.getMonth() + 1,
      billingDay
    );
  }
  if (previous) {
    end = start;
    start = billingBoundaryForMonthV89(
      end.getFullYear(),
      end.getMonth() - 1,
      billingDay
    );
  }
  return datesBetweenV89(start, addLocalDaysV89(end, -1));
};

export const selectedDatesToIntervalsV89 = (selectedDates = []) => {
  const dates = [...new Set(selectedDates)]
    .map(localDateFromKeyV89)
    .filter(Boolean)
    .sort((left, right) => left - right);
  const intervals = [];
  dates.forEach((date) => {
    const start = Math.floor(date.getTime() / 1000);
    const end = Math.floor(addLocalDaysV89(date, 1).getTime() / 1000);
    const previous = intervals[intervals.length - 1];
    if (previous && previous.end === start) {
      previous.end = end;
    } else {
      intervals.push({ start, end });
    }
  });
  return intervals;
};

export const serializeDateIntervalsV89 = (selectedDates = []) =>
  selectedDatesToIntervalsV89(selectedDates)
    .map(({ start, end }) => `${start}:${end}`)
    .join(',');

const ELIGIBILITY_STATUSES_V810 = new Set([
  'enabled',
  'disabled',
  'partial',
  'unresolved',
  'unknown',
]);

export const indexEligibilityDaysV810 = (days = []) =>
  Object.fromEntries(
    days
      .filter(
        (day) =>
          localDateFromKeyV89(day?.date) &&
          ELIGIBILITY_STATUSES_V810.has(day?.status)
      )
      .map((day) => [day.date, day])
  );

export const presetDateKeysV89 = (preset, billingDay, now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'current_cycle') {
    return billingCycleDateKeysV89(today, billingDay).filter(
      (key) => key <= localDateKeyV89(today)
    );
  }
  if (preset === 'previous_cycle')
    return billingCycleDateKeysV89(today, billingDay, true);
  if (preset === 'today') return [localDateKeyV89(today)];
  if (preset === 'yesterday')
    return [localDateKeyV89(addLocalDaysV89(today, -1))];
  const count = Number(
    String(preset).replace('last_', '').replace('_days', '')
  );
  if (![3, 7, 30, 90].includes(count)) return [];
  return datesBetweenV89(addLocalDaysV89(today, -(count - 1)), today);
};

const monthCellsV89 = (visibleMonth) => {
  const first = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1
  );
  const mondayOffset = (first.getDay() + 6) % 7;
  const gridStart = addLocalDaysV89(first, -mondayOffset);
  return Array.from({ length: 42 }, (_, index) =>
    addLocalDaysV89(gridStart, index)
  );
};

const BillingDateCalendarV89 = ({
  billingDay = 1,
  value = [],
  onChange,
  onRefreshToday,
  compact = false,
  eligibilityEndpoint = '',
}) => {
  const { t, i18n } = useTranslation();
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const last = localDateFromKeyV89(value[value.length - 1]);
    const source = last || new Date();
    return new Date(source.getFullYear(), source.getMonth(), 1);
  });
  const [multiSelect, setMultiSelect] = useState(true);
  const [eligibilityDays, setEligibilityDays] = useState({});
  const anchorRef = useRef(null);
  const selected = useMemo(() => new Set(value), [value]);
  const lastSelectedDate = value[value.length - 1];
  useEffect(() => {
    const last = localDateFromKeyV89(lastSelectedDate);
    if (last) {
      setVisibleMonth(new Date(last.getFullYear(), last.getMonth(), 1));
    }
  }, [lastSelectedDate]);
  useEffect(() => {
    onRefreshToday?.();
  }, [onRefreshToday]);
  const cells = useMemo(() => monthCellsV89(visibleMonth), [visibleMonth]);
  const visibleIntervals = useMemo(
    () => serializeDateIntervalsV89(cells.map(localDateKeyV89)),
    [cells]
  );

  useEffect(() => {
    let active = true;
    if (!eligibilityEndpoint || !visibleIntervals) {
      setEligibilityDays({});
      return () => {
        active = false;
      };
    }
    API.get(
      `${eligibilityEndpoint}?date_intervals=${encodeURIComponent(
        visibleIntervals
      )}`
    )
      .then((response) => {
        if (!active) return;
        if (!response?.data?.success) {
          setEligibilityDays({});
          return;
        }
        const days = response.data.data?.days || [];
        setEligibilityDays(indexEligibilityDaysV810(days));
      })
      .catch(() => {
        if (active) setEligibilityDays({});
      });
    return () => {
      active = false;
    };
  }, [eligibilityEndpoint, visibleIntervals]);
  const weekdays = useMemo(() => {
    const monday = new Date(2026, 7, 24);
    return Array.from({ length: 7 }, (_, index) =>
      new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(
        addLocalDaysV89(monday, index)
      )
    );
  }, [i18n.language]);
  const presetOptions = [
    'current_cycle',
    'previous_cycle',
    'today',
    'yesterday',
    'last_3_days',
    'last_7_days',
    'last_30_days',
    'last_90_days',
  ]
    .filter(
      (preset) =>
        Number(billingDay) > 0 ||
        (preset !== 'current_cycle' && preset !== 'previous_cycle')
    )
    .map((preset) => ({
      key: preset,
      value: preset,
      text: t(`billing.calendar.presets.${preset}`),
    }));

  const emit = (dates, preset = null) =>
    onChange?.([...new Set(dates)].sort(), { preset });

  const selectDate = (event, date) => {
    const key = localDateKeyV89(date);
    if (!multiSelect) {
      anchorRef.current = key;
      emit([key]);
      return;
    }
    if (event.shiftKey && anchorRef.current) {
      const anchor = localDateFromKeyV89(anchorRef.current);
      if (anchor) emit([...selected, ...datesBetweenV89(anchor, date)]);
    } else {
      const next = new Set(selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      emit([...next]);
    }
    anchorRef.current = key;
  };

  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'long',
  }).format(visibleMonth);

  return (
    <section
      className={`billing-calendar-v89${compact ? ' compact' : ''}`}
      onFocusCapture={onRefreshToday}
    >
      <div className='billing-calendar-toolbar-v89'>
        <div className='billing-calendar-month-v89'>
          <Button
            type='button'
            basic
            icon='chevron left'
            aria-label={t('billing.calendar.previous_month')}
            title={t('billing.calendar.previous_month')}
            onClick={() =>
              setVisibleMonth(
                new Date(
                  visibleMonth.getFullYear(),
                  visibleMonth.getMonth() - 1,
                  1
                )
              )
            }
          />
          <strong>{monthLabel}</strong>
          <Button
            type='button'
            basic
            icon='chevron right'
            aria-label={t('billing.calendar.next_month')}
            title={t('billing.calendar.next_month')}
            onClick={() =>
              setVisibleMonth(
                new Date(
                  visibleMonth.getFullYear(),
                  visibleMonth.getMonth() + 1,
                  1
                )
              )
            }
          />
        </div>
        <div className='billing-calendar-actions-v89'>
          <Checkbox
            toggle
            checked={multiSelect}
            label={t('billing.calendar.multi_select')}
            onChange={(_, data) => setMultiSelect(Boolean(data.checked))}
          />
          <Dropdown
            button
            className='icon'
            floating
            labeled
            icon='calendar alternate outline'
            text={t('billing.calendar.templates')}
            options={presetOptions}
            onOpen={onRefreshToday}
            onChange={(_, { value: preset }) => {
              const dates = presetDateKeysV89(preset, billingDay);
              anchorRef.current = dates[dates.length - 1] || null;
              emit(dates, preset);
              const last = localDateFromKeyV89(dates[dates.length - 1]);
              if (last)
                setVisibleMonth(
                  new Date(last.getFullYear(), last.getMonth(), 1)
                );
            }}
          />
          <Button
            type='button'
            basic
            icon='trash alternate outline'
            aria-label={t('billing.calendar.clear')}
            title={t('billing.calendar.clear')}
            onClick={() => emit([])}
          />
        </div>
      </div>
      <div className='billing-calendar-grid-v89'>
        {weekdays.map((weekday) => (
          <span className='billing-calendar-weekday-v89' key={weekday}>
            {weekday}
          </span>
        ))}
        {cells.map((date) => {
          const key = localDateKeyV89(date);
          const boundary =
            Number(billingDay) > 0
              ? billingBoundaryForMonthV89(
                  date.getFullYear(),
                  date.getMonth(),
                  billingDay
                )
              : null;
          const isBoundary = boundary && date.getDate() === boundary.getDate();
          const outside = date.getMonth() !== visibleMonth.getMonth();
          const eligibility = eligibilityDays[key];
          const transitionText = (eligibility?.transition_times || [])
            .map((timestamp) =>
              new Date(Number(timestamp) * 1000).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            )
            .join(', ');
          const pendingRequestCount = Number(
            eligibility?.pending_request_count || 0
          );
          const title = [
            key,
            eligibility?.status
              ? t(`billing.calendar.eligibility.${eligibility.status}`)
              : '',
            transitionText
              ? t('billing.calendar.transitions', { times: transitionText })
              : '',
            pendingRequestCount > 0
              ? t('billing.calendar.pending_prices', {
                  count: pendingRequestCount,
                })
              : '',
            isBoundary ? t('billing.calendar.billing_day') : '',
          ]
            .filter(Boolean)
            .join(' | ');
          return (
            <button
              type='button'
              key={key}
              className={[
                'billing-calendar-day-v89',
                selected.has(key) ? 'selected' : '',
                isBoundary ? 'billing-boundary' : '',
                eligibility?.status ? `eligibility-${eligibility.status}` : '',
                outside ? 'outside' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={selected.has(key)}
              aria-current={
                key === localDateKeyV89(new Date()) ? 'date' : undefined
              }
              title={title}
              onClick={(event) => selectDate(event, date)}
            >
              <span>{date.getDate()}</span>
              {pendingRequestCount > 0 ? (
                <span
                  className='billing-calendar-pending-v810'
                  aria-hidden='true'
                />
              ) : null}
              {isBoundary ? <i aria-hidden='true' /> : null}
            </button>
          );
        })}
      </div>
      <div className='billing-calendar-footer-v89'>
        <Label basic>
          <Icon name='check circle outline' />
          {t('billing.calendar.selected_count', { count: selected.size })}
        </Label>
        <div className='billing-calendar-legends-v810'>
          {['enabled', 'disabled', 'partial', 'unresolved', 'unknown'].map(
            (status) => (
              <span
                className={`billing-calendar-state-legend-v810 eligibility-${status}`}
                key={status}
              >
                <i aria-hidden='true' />
                {t(`billing.calendar.eligibility.${status}`)}
              </span>
            )
          )}
          {Number(billingDay) > 0 ? (
            <span className='billing-calendar-legend-v89'>
              <i aria-hidden='true' />
              {t('billing.calendar.billing_day')}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default BillingDateCalendarV89;
